"use client";

import React, { useMemo } from "react";
import { X, ChevronDown } from "lucide-react";
import { cn } from "@/utils/utils";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  AudienceFilter,
  FilterOperator,
  OPERATOR_LABELS,
  FilterValue,
} from "../../types/audienceTypes";
import { getFieldById } from "../../config/filterFields";

// ============================================================================
// Component Props
// ============================================================================

interface FilterRowProps {
  filter: AudienceFilter;
  onUpdate: (updates: Partial<AudienceFilter>) => void;
  onRemove: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function FilterRow({ filter, onUpdate, onRemove }: FilterRowProps) {
  const field = useMemo(() => getFieldById(filter.fieldId), [filter.fieldId]);

  if (!field) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
        <span className="text-sm text-red-400">Unknown field: {filter.fieldId}</span>
        <button
          className="ml-auto p-1 rounded text-red-400 hover:text-red-300"
          onClick={onRemove}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Get available operators for this field
  const operators = field.operators;

  // Render value input based on field type and operator
  const renderValueInput = () => {
    // Some operators don't need a value
    if (["is_known", "is_unknown", "is_true", "is_false"].includes(filter.operator)) {
      return null;
    }

    switch (field.type) {
      case "text":
        return (
          <Input
            className="ab-input h-9 w-48 text-sm"
            onChange={(e) => onUpdate({ value: e.target.value })}
            placeholder={field.placeholder || "Enter value..."}
            value={(filter.value as string) || ""}
          />
        );

      case "number":
        if (filter.operator === "between") {
          const rangeValue = (filter.value as { from: number; to: number }) || { from: 0, to: 0 };
          return (
            <div className="flex items-center gap-2">
              <Input
                className="ab-input h-9 w-20 text-sm"
                onChange={(e) =>
                  onUpdate({
                    value: { ...rangeValue, from: parseFloat(e.target.value) || 0 },
                  })
                }
                placeholder="From"
                type="number"
                value={rangeValue.from || ""}
              />
              <span className="text-[var(--ab-text-muted)] text-sm">and</span>
              <Input
                className="ab-input h-9 w-20 text-sm"
                onChange={(e) =>
                  onUpdate({
                    value: { ...rangeValue, to: parseFloat(e.target.value) || 0 },
                  })
                }
                placeholder="To"
                type="number"
                value={rangeValue.to || ""}
              />
            </div>
          );
        }
        return (
          <Input
            className="ab-input h-9 w-32 text-sm"
            onChange={(e) => onUpdate({ value: parseFloat(e.target.value) || 0 })}
            placeholder="0"
            type="number"
            value={(filter.value as number) || ""}
          />
        );

      case "date":
        if (["is_more_than_days_ago", "is_less_than_days_ago"].includes(filter.operator)) {
          return (
            <div className="flex items-center gap-2">
              <Input
                className="ab-input h-9 w-20 text-sm"
                min="1"
                onChange={(e) => onUpdate({ value: parseInt(e.target.value) || 1 })}
                placeholder="30"
                type="number"
                value={(filter.value as number) || ""}
              />
              <span className="text-[var(--ab-text-muted)] text-sm">days ago</span>
            </div>
          );
        }
        if (filter.operator === "is_between") {
          const rangeValue = (filter.value as { from: string; to: string }) || { from: "", to: "" };
          return (
            <div className="flex items-center gap-2">
              <Input
                className="ab-input h-9 w-36 text-sm"
                onChange={(e) =>
                  onUpdate({
                    value: { ...rangeValue, from: e.target.value },
                  })
                }
                type="date"
                value={rangeValue.from || ""}
              />
              <span className="text-[var(--ab-text-muted)] text-sm">and</span>
              <Input
                className="ab-input h-9 w-36 text-sm"
                onChange={(e) =>
                  onUpdate({
                    value: { ...rangeValue, to: e.target.value },
                  })
                }
                type="date"
                value={rangeValue.to || ""}
              />
            </div>
          );
        }
        return (
          <Input
            className="ab-input h-9 w-40 text-sm"
            onChange={(e) => onUpdate({ value: e.target.value })}
            type="date"
            value={(filter.value as string) || ""}
          />
        );

      case "select":
      case "multi_select":
        if (!field.options || field.options.length === 0) {
          return (
            <Input
              className="ab-input h-9 w-48 text-sm"
              onChange={(e) => onUpdate({ value: e.target.value })}
              placeholder="Enter value..."
              value={(filter.value as string) || ""}
            />
          );
        }

        const selectedValues = Array.isArray(filter.value)
          ? filter.value
          : filter.value
          ? [filter.value]
          : [];

        return (
          <div className="relative">
            <Select
              onValueChange={(val) => {
                if (field.type === "multi_select") {
                  const current = selectedValues as (string | number)[];
                  if (current.includes(val)) {
                    onUpdate({ value: current.filter((v) => v !== val) as string[] });
                  } else {
                    onUpdate({ value: [...current, val] as string[] });
                  }
                } else {
                  onUpdate({ value: val });
                }
              }}
              value={field.type === "select" ? (filter.value as string) : undefined}
            >
              <SelectTrigger className="ab-input h-9 w-48 text-sm">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent className="bg-[var(--ab-bg-secondary)] border-[var(--ab-border)]">
                {field.options.map((option) => (
                  <SelectItem
                    className="text-[var(--ab-text-primary)] hover:bg-[var(--ab-bg-tertiary)] focus:bg-[var(--ab-bg-tertiary)]"
                    key={option.id}
                    value={String(option.id)}
                  >
                    {option.icon ? `${option.icon} ${option.label}` : option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Multi-select badges */}
            {field.type === "multi_select" && selectedValues.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {(selectedValues as (string | number)[]).map((val) => {
                  const option = field.options?.find((o) => String(o.id) === String(val));
                  return (
                    <Badge
                      className="bg-[var(--ab-accent)]/20 text-[var(--ab-accent)] border-[var(--ab-accent)]/30 hover:bg-[var(--ab-accent)]/30 cursor-pointer"
                      key={String(val)}
                      onClick={() => {
                        onUpdate({
                          value: (selectedValues as (string | number)[]).filter(
                            (v) => v !== val
                          ) as string[],
                        });
                      }}
                    >
                      {option?.icon && <span className="mr-1">{option.icon}</span>}
                      {option?.label || val}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-[var(--ab-bg-primary)] border border-[var(--ab-border)] hover:border-[var(--ab-border-hover)] transition-all group">
      {/* Field Label */}
      <div className="flex-shrink-0">
        <div className="h-9 px-3 flex items-center rounded-lg bg-[var(--ab-bg-tertiary)] border border-[var(--ab-border)]">
          <span className="text-sm font-medium text-[var(--ab-text-primary)]">
            {field.label}
          </span>
        </div>
      </div>

      {/* Operator Select */}
      <div className="flex-shrink-0">
        <Select
          onValueChange={(val) => onUpdate({ operator: val as FilterOperator })}
          value={filter.operator}
        >
          <SelectTrigger className="ab-input h-9 w-40 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[var(--ab-bg-secondary)] border-[var(--ab-border)]">
            {operators.map((op) => (
              <SelectItem
                className="text-[var(--ab-text-primary)] hover:bg-[var(--ab-bg-tertiary)] focus:bg-[var(--ab-bg-tertiary)]"
                key={op}
                value={op}
              >
                {OPERATOR_LABELS[op]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Value Input */}
      <div className="flex-1">{renderValueInput()}</div>

      {/* Remove Button */}
      <button
        className="flex-shrink-0 p-2 rounded-lg text-[var(--ab-text-muted)] hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
        onClick={onRemove}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default FilterRow;
